import React, { useEffect } from 'react';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';
import { alpha, Box } from '@mui/material';
import { LoadingText } from '@ui/LoadingText';
import {
  useProjectOpening,
  useProjectClosing,
} from '@features/projects/project-opening.store';
import { makeStyles } from 'tss-react/mui';
import Loading from '@ui/Loading';
import EditProductDialog from '@features/products/EditProduct/EditProductDialog';
import ProductSelector from '@features/products/EditProduct/ProductSelector';
import EditProjectDialog from '@features/projects/EditProject/EditProjectDialog';
import ImportProjectDialog from '@features/projects/ImportProjectDialog';
import { ProjectErrors } from '@features/projects/ProjectErrors';
import ProjectSelector from '@features/projects/ProjectSelector';
import { useDocumentTitle } from '@client/hooks/use-document-title.hook';
import { useGlobalKeyListener } from '@client/hooks/use-global-key-listener.hook';
import { useIsProjectList } from '@client/hooks/use-router.hooks';
import { useInitUndoRedo } from '@client/hooks/use-undo-redo.hooks';
import { useProductStoreState } from '@client/store/product';
import { useProjectState } from '@client/store/project';
import { useUIState } from '@client/store/ui';
import { Page } from '@client/store/ui/ui.model';
import { AppBar } from './components/AppBar';
import ProjectView from './components/ProjectView';
import { Paywall } from '@features/projects/Paywall/Paywall';

const useStyles = makeStyles<{ currentPage: Page }>()(({ palette }) => ({
  root: {
    display: 'flex',
    // Default canvas behind every transparent surface in MainView.
    backgroundColor: palette.warmSurface.main,
  },
}));

const MainView: React.FC = () => {
  const showExitLoading = useProjectClosing((state) => state.showLoading);
  useDocumentTitle();
  useGlobalKeyListener();
  const { selectedPage } = useUIState('selectedPage');
  const { classes } = useStyles({ currentPage: selectedPage });

  const { isCreating: creatingProduct } = useProductStoreState('isCreating');

  const isProjectList = useIsProjectList();
  const openingPhase = useProjectOpening((state) => state.phase);
  const opening = openingPhase !== 'idle';
  const openingError = useProjectOpening((state) => state.error);
  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    if (openingError) enqueueSnackbar(openingError, { variant: 'error' });
  }, [openingError, enqueueSnackbar]);

  useInitUndoRedo();

  const { isCreating: isCreatingProject } = useProjectState('isCreating');

  return (
    <div className={classes.root} data-project-opening-phase={openingPhase}>
      {(isProjectList || opening) && <ProjectSelector />}
      <Box
        data-project-opening-surface=""
        inert={opening}
        sx={
          opening && !isProjectList
            ? {
                position: 'fixed',
                inset: 0,
                zIndex: 2000,
                opacity: 0,
                pointerEvents: 'none',
                display: 'flex',
                backgroundColor: 'warmSurface.main',
              }
            : { display: 'contents' }
        }
      >
        <AppBar />
        {!isProjectList && <ProjectView />}
      </Box>
      {opening && openingPhase !== 'revealing' && (
        <Box
          data-project-opening-loader=""
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 2001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: (theme) =>
              alpha(theme.palette.warmSurface.main, 0.25),
          }}
        >
          <LoadingText text="Loading project" />
        </Box>
      )}
      <ProjectErrors />
      {showExitLoading && (
        <Box
          data-project-exit-loader=""
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 2001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <LoadingText text="Loading projects" />
        </Box>
      )}

      <Loading open={creatingProduct || isCreatingProject} />

      <ProductSelector />
      <ImportProjectDialog />
      <EditProjectDialog />
      <EditProductDialog />
      <Paywall />
    </div>
  );
};

export default MainView;
